import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { BrowserProvider } from 'ethers';
import { web3API } from '@/lib/api';

const WalletContext = createContext(null);

const CHAINS = {
  ethereum: { id: '0x1', name: 'Ethereum', currency: 'ETH' },
  bsc: { id: '0x38', name: 'BSC', currency: 'BNB' },
  polygon: { id: '0x89', name: 'Polygon', currency: 'MATIC' },
  tron: { id: null, name: 'TRON', currency: 'TRX' },
};

export function WalletProvider({ children }) {
  const [connectedWallet, setConnectedWallet] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');

  // Check if MetaMask is installed
  const hasMetaMask = typeof window !== 'undefined' && typeof window.ethereum !== 'undefined';

  // Check if TronLink is installed
  const hasTronLink = typeof window !== 'undefined' && (typeof window.tronWeb !== 'undefined' || typeof window.tronLink !== 'undefined');

  const connectMetaMask = useCallback(async () => {
    setConnecting(true);
    setError('');
    try {
      if (!window.ethereum) {
        throw new Error('MetaMask bulunamadi. Lutfen MetaMask yukleyin.');
      }

      const provider = new BrowserProvider(window.ethereum);
      const accounts = await provider.send('eth_requestAccounts', []);

      if (!accounts || accounts.length === 0) {
        throw new Error('Hesap secilmedi');
      }

      const address = accounts[0];
      const network = await provider.getNetwork();
      let chain = 'ethereum';
      if (network.chainId === 56n) chain = 'bsc';
      else if (network.chainId === 137n) chain = 'polygon';

      // Get challenge
      const challengeRes = await web3API.getChallenge({
        wallet_address: address,
        wallet_type: 'metamask',
        chain,
      });

      // Sign message
      const signer = await provider.getSigner();
      const signature = await signer.signMessage(challengeRes.data.challenge);

      const walletInfo = {
        address,
        chain,
        wallet_type: 'metamask',
        signature,
        challenge: challengeRes.data.challenge,
        provider,
        signer,
      };

      setConnectedWallet(walletInfo);
      return walletInfo;
    } catch (err) {
      const msg = err.code === 4001 ? 'Kullanici reddetti' : err.message || 'Baglanti basarisiz';
      setError(msg);
      throw err;
    } finally {
      setConnecting(false);
    }
  }, []);

  const connectTronLink = useCallback(async () => {
    setConnecting(true);
    setError('');
    try {
      if (!window.tronWeb && !window.tronLink) {
        throw new Error('TronLink bulunamadi. Lutfen TronLink yukleyin.');
      }

      // Request access
      if (window.tronLink && window.tronLink.request) {
        await window.tronLink.request({ method: 'tron_requestAccounts' });
      }

      // Wait for TronWeb to be ready
      let tronWeb = window.tronWeb;
      if (!tronWeb || !tronWeb.defaultAddress || !tronWeb.defaultAddress.base58) {
        // Wait a bit for TronLink to inject
        await new Promise((resolve) => setTimeout(resolve, 1500));
        tronWeb = window.tronWeb;
      }

      if (!tronWeb || !tronWeb.defaultAddress || !tronWeb.defaultAddress.base58) {
        throw new Error('TronLink hesabi bulunamadi. Lutfen TronLink\'a giris yapin.');
      }

      const address = tronWeb.defaultAddress.base58;

      // Get challenge
      const challengeRes = await web3API.getChallenge({
        wallet_address: address,
        wallet_type: 'tronlink',
        chain: 'tron',
      });

      // Sign message with TronLink
      const hexMsg = tronWeb.toHex(challengeRes.data.challenge);
      const signature = await tronWeb.trx.signMessageV2(challengeRes.data.challenge);

      const walletInfo = {
        address,
        chain: 'tron',
        wallet_type: 'tronlink',
        signature,
        challenge: challengeRes.data.challenge,
        tronWeb,
      };

      setConnectedWallet(walletInfo);
      return walletInfo;
    } catch (err) {
      const msg = err.message || 'TronLink baglantisi basarisiz';
      setError(msg);
      throw err;
    } finally {
      setConnecting(false);
    }
  }, []);

  const connectWalletConnect = useCallback(async () => {
    setConnecting(true);
    setError('');
    try {
      // Dynamic import to avoid SSR issues
      const { EthereumProvider } = await import('@walletconnect/ethereum-provider');

      const projectId = process.env.REACT_APP_WALLETCONNECT_PROJECT_ID || '4c3b2c3d5e6f7a8b9c0d1e2f3a4b5c6d';

      const wcProvider = await EthereumProvider.init({
        projectId,
        chains: [1],
        optionalChains: [56, 137],
        showQrModal: true,
        metadata: {
          name: 'Safe Crypto',
          description: 'Guvenli Kripto Yatirim Portali',
          url: window.location.origin,
          icons: [`${window.location.origin}/favicon.ico`],
        },
      });

      await wcProvider.connect();

      const accounts = wcProvider.accounts;
      if (!accounts || accounts.length === 0) {
        throw new Error('Hesap secilmedi');
      }

      const address = accounts[0];
      const chainId = wcProvider.chainId;
      let chain = 'ethereum';
      if (chainId === 56) chain = 'bsc';
      else if (chainId === 137) chain = 'polygon';

      const ethersProvider = new BrowserProvider(wcProvider);

      // Get challenge
      const challengeRes = await web3API.getChallenge({
        wallet_address: address,
        wallet_type: 'walletconnect',
        chain,
      });

      // Sign message
      const signer = await ethersProvider.getSigner();
      const signature = await signer.signMessage(challengeRes.data.challenge);

      const walletInfo = {
        address,
        chain,
        wallet_type: 'walletconnect',
        signature,
        challenge: challengeRes.data.challenge,
        provider: ethersProvider,
        wcProvider,
        signer,
      };

      setConnectedWallet(walletInfo);
      return walletInfo;
    } catch (err) {
      const msg = err.message || 'WalletConnect baglantisi basarisiz';
      setError(msg);
      throw err;
    } finally {
      setConnecting(false);
    }
  }, []);

  const disconnect = useCallback(() => {
    if (connectedWallet?.wcProvider) {
      connectedWallet.wcProvider.disconnect().catch(() => {});
    }
    setConnectedWallet(null);
    setError('');
  }, [connectedWallet]);

  // Listen for MetaMask account changes
  useEffect(() => {
    if (window.ethereum) {
      const handleAccountsChanged = (accounts) => {
        if (accounts.length === 0) {
          disconnect();
        }
      };
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      return () => window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
    }
  }, [disconnect]);

  return (
    <WalletContext.Provider
      value={{
        connectedWallet,
        connecting,
        error,
        hasMetaMask,
        hasTronLink,
        connectMetaMask,
        connectTronLink,
        connectWalletConnect,
        disconnect,
        setError,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
}

export function useWallet() {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
}
